import { useCallback, useEffect, useState } from "react";

export const useTimer = (duration: number, storageKey: string) => {
  const calculateRemaining = useCallback(() => {
    const savedExpiry = sessionStorage.getItem(storageKey);
    if (!savedExpiry) return null;
    if (savedExpiry === "expired") return 0;

    const expiry = parseInt(savedExpiry, 10);
    if (expiry < 1000000000000) return null;

    const remaining = Math.ceil((expiry - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  }, [storageKey]);

  const [timeLeft, setTimeLeft] = useState(() => {
    return calculateRemaining() ?? duration;
  });

  useEffect(() => {
    if (calculateRemaining() === null) {
      const expiry = Date.now() + duration * 1000;
      sessionStorage.setItem(storageKey, expiry.toString());
      setTimeLeft(duration);
    }
  }, [duration, storageKey, calculateRemaining]);

  const hasTimeLeft = timeLeft > 0;

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    const syncTimer = () => {
      const remaining = calculateRemaining();
      if (remaining !== null) {
        setTimeLeft(remaining);
        if (remaining <= 0) {
          sessionStorage.setItem(storageKey, "expired");
          if (interval) clearInterval(interval);
        }
      }
    };

    if (hasTimeLeft) {
      interval = setInterval(syncTimer, 1000);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasTimeLeft, storageKey, calculateRemaining]);

  const start = useCallback(() => {
    const expiry = Date.now() + duration * 1000;
    sessionStorage.setItem(storageKey, expiry.toString());
    setTimeLeft(duration);
  }, [duration, storageKey]);

  const reset = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setTimeLeft(0);
  }, [storageKey]);

  return {
    timeLeft,
    isExpired: timeLeft === 0,
    start,
    reset,
  };
};
